import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {getChat, getChatByUserId, getChats, markChatAsRead as markChatAsReadApi} from "../http/chatAPI";
import {Context} from "../index";
import dayjs from "dayjs";

export const useChats = (categoryId = null) => {
        const {user} = useContext(Context);
        const [closedChats, setClosedChats] = useState([]);
        const [openedChats, setOpenedChats] = useState([]);
        const [myChats, setMyChats] = useState([]);
        
        const [offsets, setOffsets] = useState({my: 0, opened: 0, closed: 0});
        const [hasMore, setHasMore] = useState({my: true, opened: true, closed: true});
        const [search, setSearch] = useState({my: "", opened: "", closed: ""});
        const [sortBy, setSortBy] = useState({my: "recent", opened: "recent", closed: "recent"});
        const [filterByUnread, setFilterByUnread] = useState({my: false, opened: false, closed: false});
        
        // Храним актуальное значение categoryId, sortBy и filterByUnread в ref для использования в колбэках
        const categoryIdRef = useRef(categoryId);
        const sortByRef = useRef(sortBy);
        const filterByUnreadRef = useRef(filterByUnread);
        useEffect(() => {
            categoryIdRef.current = categoryId;
            sortByRef.current = sortBy;
            filterByUnreadRef.current = filterByUnread;
        }, [categoryId, sortBy, filterByUnread]);

        const [selectedChat, setSelectedChat] = useState(null);
        const [loading, setLoading] = useState(false);
        const [resetLoading, setResetLoading] = useState(false);
        const [changingChat, setChangingChat] = useState(false);
        const [error, setError] = useState(null);

        const updateChatData = (chat) => {
            _updateChatInAllArrays(chat);
        }

        const _sortChats = (currentSortBy) => (a, b) => {
            // При сортировке по заявкам - приоритет заявкам
            if (currentSortBy === 'requests') {
                // Сначала чаты с активными заявками
                if (a.hasActiveRequests !== b.hasActiveRequests) {
                    return a.hasActiveRequests ? -1 : 1;
                }
                // Для чатов с заявками сортируем по дате последней заявки
                if (a.hasActiveRequests && b.hasActiveRequests) {
                    const aRequestDate = dayjs(a.lastRequestDate);
                    const bRequestDate = dayjs(b.lastRequestDate);
                    if (!aRequestDate.isSame(bRequestDate)) {
                        return bRequestDate.isAfter(aRequestDate) ? 1 : -1;
                    }
                }
                // После заявок - чаты с сообщениями внизу, без сообщений еще ниже
                const aHasMessages = !!a.lastMessageDateTime;
                const bHasMessages = !!b.lastMessageDateTime;
                
                if (aHasMessages !== bHasMessages) {
                    return aHasMessages ? -1 : 1;
                }
                
                // Если оба с сообщениями или оба без - сортируем по дате сообщения
                if (aHasMessages && bHasMessages) {
                    const aMessageDate = dayjs(a.lastMessageDateTime);
                    const bMessageDate = dayjs(b.lastMessageDateTime);
                    return bMessageDate.isAfter(aMessageDate) ? 1 : -1;
                }
            } else {
                const aDate = a.lastMessageDateTime || a.lastRequestDate;
                const bDate = b.lastMessageDateTime || b.lastRequestDate;
                const aHasActivity = !!aDate;
                const bHasActivity = !!bDate;

                if (aHasActivity !== bHasActivity) {
                    return aHasActivity ? -1 : 1;
                }

                return dayjs(bDate).isAfter(dayjs(aDate)) ? 1 : -1;
            }
            
            return 0;
        };


        const _updateChatInAllArrays = (chat) => {
            // Проверяем, что чат принадлежит текущей категории
            const chatBelongsToCategory = !categoryIdRef.current || chat.categoryId === categoryIdRef.current;
            
            // console.log('_updateChatInAllArrays:', {
            //     chatId: chat.id,
            //     chatCategoryId: chat.categoryId,
            //     currentCategoryId: categoryId,
            //     chatBelongsToCategory
            // });
            
            setMyChats(prev => {
                const exists = prev.some(c => c.id === chat.id);
                if (exists) {
                    // Если чат уже в списке, обновляем или удаляем в зависимости от категории
                    if (chatBelongsToCategory) {
                        return prev.map(c => c.id === chat.id ? chat : c).sort(_sortChats(sortByRef.current.my));
                    } else {
                        return prev.filter(c => c.id !== chat.id);
                    }
                }
                // Добавляем чат, если он должен быть в этом списке
                if (chatBelongsToCategory && chat.adminId === user.user.id) {
                    return [chat, ...prev].sort(_sortChats(sortByRef.current.my));
                }
                return prev;
            });

            setOpenedChats(prev => {
                const exists = prev.some(c => c.id === chat.id);
                if (exists) {
                    if (chatBelongsToCategory) {
                        return prev.map(c => c.id === chat.id ? chat : c).sort(_sortChats(sortByRef.current.opened));
                    } else {
                        return prev.filter(c => c.id !== chat.id);
                    }
                }
                // Добавляем чат в открытые, если он открыт
                if (chatBelongsToCategory && chat.adminId !== null) {
                    return [chat, ...prev].sort(_sortChats(sortByRef.current.opened));
                }
                return prev;
            });

            setClosedChats(prev => {
                const exists = prev.some(c => c.id === chat.id);
                if (exists) {
                    if (chatBelongsToCategory) {
                        return prev.map(c => c.id === chat.id ? chat : c).sort(_sortChats(sortByRef.current.closed));
                    } else {
                        return prev.filter(c => c.id !== chat.id);
                    }
                }
                // Добавляем чат в свободные, если он закрыт
                if (chatBelongsToCategory && chat.adminId === null) {
                    return [chat, ...prev].sort(_sortChats(sortByRef.current.closed));
                }
                return prev;
            });

            setSelectedChat(prev => prev?.id === chat.id ? chat : prev);
        }

        const loadChats = async (type, search, reset = false) => {
            if (!hasMore[type] && !reset) return;

            setLoading(true);
            setResetLoading(!!reset);
            let limit = 20;
            const offset = reset ? 0 : offsets[type];

            // Запоминаем категорию для которой делаем запрос
            const requestCategoryId = categoryIdRef.current;

            const response = await getChats(user.user.id, type, search, limit, offset, sortBy[type], filterByUnread[type], requestCategoryId);
            
            // Проверяем, что категория не изменилась пока выполнялся запрос
            if (requestCategoryId !== categoryIdRef.current) {
                // Категория изменилась - игнорируем результат
                setLoading(false);
                setResetLoading(false);
                return;
            }
            
            const data = response.chats || response; // Поддержка старого и нового формата
            
            const newChatIds = data.map(chat => chat.id);

            switch (type) {
                case 'my':
                    setMyChats(prev => reset
                        ? data
                        : [...prev.filter(c => !newChatIds.includes(c.id)), ...data]
                    );
                    break;
                case 'opened':
                    setOpenedChats(prev => reset
                        ? data
                        : [...prev.filter(c => !newChatIds.includes(c.id)), ...data]
                    );
                    break;
                case 'closed':
                    setClosedChats(prev => reset
                        ? data
                        : [...prev.filter(c => !newChatIds.includes(c.id)), ...data]
                    );
                    break;
            }

            if (data.length < limit) {
                setHasMore(prev => ({...prev, [type]: false}));
            } else if (reset) {
                setHasMore(prev => ({...prev, [type]: true}));
            }

            setOffsets(prev => ({
                ...prev,
                [type]: reset ? data.length : prev[type] + data.length
            }));

            data.forEach(updateChatData);
            setLoading(false);
            setResetLoading(false);
        };

        const handleChatTaken = async (chatId) => {
            const chatDb = await getChat(chatId);

            if (selectedChat?.id === chatId) {
                setSelectedChat(chatDb);
            }

            // Проверяем, что чат принадлежит текущей категории
            const chatBelongsToCategory = !categoryIdRef.current || chatDb.categoryId === categoryIdRef.current;

            if (chatDb.adminId === user.user.id) {
                setMyChats(prev => {
                    // Определяем, соответствует ли чат текущему фильтру
                    let matchesFilter = true;
                    if (filterByUnreadRef.current.my) {
                        if (sortByRef.current.my === 'requests') {
                            // При сортировке по заявкам фильтруем по наличию необработанных заявок
                            matchesFilter = chatDb.hasActiveRequests;
                        } else {
                            // При сортировке по сообщениям фильтруем по непрочитанности
                            matchesFilter = !chatDb.isRead && !!chatDb.lastMessageDateTime;
                        }
                    }

                    const shouldAddToList = chatBelongsToCategory && matchesFilter;

                    if (shouldAddToList) {
                        const filtered = prev.filter(c => c.id !== chatId);
                        return [chatDb, ...filtered].sort(_sortChats(sortByRef.current.my));
                    }

                    return prev.filter(c => c.id !== chatId);
                });
            } else {
                setMyChats(prev => prev.filter(chat => chat.id !== chatId));
            }

            setOpenedChats(prev => {
                // Определяем, соответствует ли чат текущему фильтру
                let matchesFilter = true;
                if (filterByUnreadRef.current.opened) {
                    if (sortByRef.current.opened === 'requests') {
                        // При сортировке по заявкам фильтруем по наличию необработанных заявок
                        matchesFilter = chatDb.hasActiveRequests;
                    } else {
                        // При сортировке по сообщениям фильтруем по непрочитанности
                        matchesFilter = !chatDb.isRead && !!chatDb.lastMessageDateTime;
                    }
                }

                const shouldAddToList = chatBelongsToCategory && matchesFilter;

                if (shouldAddToList) {
                    const filtered = prev.filter(c => c.id !== chatId);
                    return [chatDb, ...filtered].sort(_sortChats(sortByRef.current.opened));
                }

                return prev.filter(c => c.id !== chatId);
            });
            setClosedChats(prev => prev.filter(c => c.id !== chatId));
        }

        const handleChatClosed = async (chatId) => {
            const chatDb = await getChat(chatId);

            if (selectedChat?.id === chatId) {
                setSelectedChat(chatDb);
            }

            // Проверяем, что чат принадлежит текущей категории
            const chatBelongsToCategory = !categoryIdRef.current || chatDb.categoryId === categoryIdRef.current;

            setMyChats(prev => prev.filter(chat => chat.id !== chatId));
            setOpenedChats(prev => prev.filter(c => c.id !== chatId));

            setClosedChats(prev => {
                // Определяем, соответствует ли чат текущему фильтру
                let matchesFilter = true;
                if (filterByUnreadRef.current.closed) {
                    if (sortByRef.current.closed === 'requests') {
                        // При сортировке по заявкам фильтруем по наличию необработанных заявок
                        matchesFilter = chatDb.hasActiveRequests;
                    } else {
                        // При сортировке по сообщениям фильтруем по непрочитанности
                        matchesFilter = !chatDb.isRead && !!chatDb.lastMessageDateTime;
                    }
                }

                const shouldAddToList = chatBelongsToCategory && matchesFilter;

                if (shouldAddToList) {
                    const filtered = prev.filter(c => c.id !== chatId);
                    return [chatDb, ...filtered].sort(_sortChats(sortByRef.current.closed));
                }

                // Если чат не соответствует фильтру - не добавляем его в список
                return prev.filter(c => c.id !== chatId);
            });
        }

        const updateLastMessage = useCallback(async (chatId) => {
            const chatDb = await getChat(chatId);
            // console.log('updateLastMessage - chatDb:', chatDb, 'current categoryId:', categoryIdRef.current);
            if (chatDb) {
                _updateChatInAllArrays(chatDb);
            }
        }, []);

        const updateLastMessageByUserId = useCallback(async (userId) => {
            const chatDb = await getChatByUserId(userId, categoryIdRef.current);
            // console.log('updateLastMessageByUserId - chatDb:', chatDb, 'current categoryId:', categoryIdRef.current);
            if (chatDb) {
                _updateChatInAllArrays(chatDb);
            }
        }, []);

        const refreshChat = useCallback(async (chatId) => {
            const chat = await getChat(chatId);

            setMyChats(prev => {
                return prev.map(c => {
                    if (c.id === chatId) {
                        return chat;
                    }
                    return c;
                })
            });

            setOpenedChats(prev => {
                return prev.map(c => {
                    if (c.id === chatId) {
                        return chat;
                    }
                    return c;
                })
            });

            setClosedChats(prev => {
                return prev.map(c => {
                    if (c.id === chatId) {
                        return chat;
                    }
                    return c;
                })
            });
            if (chat.id === selectedChat?.id) setSelectedChat(chat);
            return chat;
        }, [selectedChat, myChats, openedChats, closedChats]);

        const selectChat = useCallback(async (chatId) => {
            const chat = await getChat(chatId);
            setSelectedChat(chat);
        }, []);

        const markAsRead = useCallback(async (chatId) => {
            try {
                await markChatAsReadApi(chatId);
            } catch (e) {
                console.error('Ошибка при пометке чата как прочитанного:', e);
                return;
            }
            const now = new Date().toISOString();
            const applyRead = (chats) => chats.map(c =>
                c.id === chatId ? { ...c, isRead: true, lastReadAt: now } : c
            );
            setMyChats(applyRead);
            setOpenedChats(applyRead);
            setClosedChats(applyRead);
            setSelectedChat(prev => prev?.id === chatId ? { ...prev, isRead: true, lastReadAt: now } : prev);
        }, []);

        const handleChatUpdated = useCallback(({ chatId, isRead, lastReadAt }) => {
            const applyUpdate = (chats) => chats.map(c =>
                c.id === chatId ? { ...c, isRead, lastReadAt } : c
            );
            setMyChats(applyUpdate);
            setOpenedChats(applyUpdate);
            setClosedChats(applyUpdate);
            setSelectedChat(prev => prev?.id === chatId ? { ...prev, isRead, lastReadAt } : prev);
        }, []);

        // const markAsRead = useCallback((chatId, value) => {
        //     setAllChats(prev => {
        //         return prev.map(chat => {
        //             if (chat.id === chatId) {
        //                 chat.isRead = value;
        //             }
        //             return chat;
        //         })
        //     });
        // }, []);

// Получение данных конкретного чата
//         const getChatData = useCallback((chatId) => {
//             const chat = allChats.find(c => c.id === chatId);
//             const messageInfo = chatMessages[chatId];
//             const managerInfo = chatManagers[chatId];
//
//             return {
//                 ...chat,
//                 lastMessage: messageInfo?.message,
//                 lastMessageDate: messageInfo?.date,
//                 adminId: managerInfo?.adminId,
//                 adminName: managerInfo?.adminName
//             };
//         }, [allChats, chatMessages, chatManagers]);

        // useEffect(() => {
        //
        //
        //     setMyChats(prev => prev.map(c => {
        //         return {...c, lastMessage: chatMessages[c.id]?.message, lastMessageDateTime: chatMessages[c.id]?.date};
        //     }));
        //
        //     setOpenedChats(prev => prev.map(c => {
        //         return {...c, lastMessage: chatMessages[c.id]?.message, lastMessageDateTime: chatMessages[c.id]?.date};
        //     }));
        //
        //     setClosedChats(prev => prev.map(c => {
        //         return {...c, lastMessage: chatMessages[c.id]?.message, lastMessageDateTime: chatMessages[c.id]?.date};
        //     }));
        //     sortChats()
        // }, [chatMessages]);


// Эффект для первоначальной загрузки
// useEffect(() => {
//     loadingChats();
// }, []);

        // Сбрасываем и перезагружаем чаты при смене categoryId
        useEffect(() => {
            setMyChats([]);
            setOpenedChats([]);
            setClosedChats([]);
            setOffsets({my: 0, opened: 0, closed: 0});
            setHasMore({my: true, opened: true, closed: true});
            setSelectedChat(null);
        }, [categoryId]);

        return {
            // Состояния
            selectedChat,
            loading,
            resetLoading,
            error,
            search,
            sortBy,
            filterByUnread,
            closedChats,
            openedChats,
            myChats,
            changingChat,
            hasMore,

            // Методы
            setChangingChat,
            setSearch,
            setSortBy,
            setFilterByUnread,
            loadChats,
            handleChatClosed,
            handleChatTaken,
            updateLastMessage,
            updateLastMessageByUserId,
            updateChatData,
            refreshChat,
            selectChat,
            markAsRead,
            handleChatUpdated,
        };
    }
;
